import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { REFERRAL_STAGE_MAX_STATUSES } from '@/lib/constants'
import { generateUUID } from '@/lib/helpers/helperFunctions'
import referralStageService from '@/lib/services/referralStageService'
import { RootState } from '@/redux/store'
import { ReferralStatusEntity } from '@/types/entities'
import { ReferralStage } from '@/types/misc'

export type ReferralStatusesItemState =
  | 'pristine'
  | 'modified'
  | 'new'
  | 'scheduled'
  | 'processing'
  | 'saved'
  | 'error'

export type ReferralStatusesItem = {
  refUUID: string
  state: ReferralStatusesItemState
  referralStatusEntity: ReferralStatusEntity | undefined
  name: string
  editName: boolean
  sortOrder: number
  isFirst: boolean
  isLast: boolean
  stage: ReferralStage
  error: string | undefined
}

export type ReferralStatusesStageCounts = Record<ReferralStage, number>

export type ReferralStatusesItemPartial = {
  refUUID: string
} & Partial<ReferralStatusesItem>

export interface ReferralStatusesState {
  updateTimeoutReference?: NodeJS.Timeout
  stageCounts: ReferralStatusesStageCounts
  items: ReferralStatusesItem[]
}

const INITIAL_STAGE_COUNTS = {
  awaiting_client_consent: 0,
  awaiting_client_schedule_meeting: 0,
  referred: 0,
  engaging: 0,
  converting: 0,
  closed_won_recurring: 0,
  closed_won_one_off: 0,
  stalled: 0,
  closed_lost: 0,
  not_sure: 0,
}

const INITIAL_STATE: ReferralStatusesState = {
  stageCounts: { ...INITIAL_STAGE_COUNTS },
  items: [],
}

const referralStatuses = createSlice({
  name: 'referralStatuses',
  initialState: INITIAL_STATE,
  reducers: {
    addNewItem: (
      state,
      action: PayloadAction<ReferralStatusesItem['stage']>,
    ) => {
      if (state.stageCounts[action.payload] >= REFERRAL_STAGE_MAX_STATUSES) {
        return
      }
      // Place the new item at the end of its stage by sorting it just past
      // the highest existing sortOrder in the stage. updateSortOrder then
      // renumbers everything in 10-unit increments.
      const baseSortOrder = referralStageService.getBaseSortOrder(
        action.payload,
      )
      const lastSortOrderInStage = state.items
        .filter((existing) => existing.stage === action.payload)
        .reduce(
          (max, existing) => Math.max(max, existing.sortOrder),
          baseSortOrder,
        )
      const item: ReferralStatusesItem = {
        refUUID: generateUUID(),
        error: undefined,
        isFirst: false,
        isLast: false,
        name: '',
        editName: true,
        referralStatusEntity: undefined,
        sortOrder: lastSortOrderInStage + 10,
        stage: action.payload,
        state: 'new',
      }
      state.items.push(item)
      updateSortOrder(state)
      updateStageStats(state)
    },
    detectItemState: (
      state: ReferralStatusesState,
      action: PayloadAction<Pick<ReferralStatusesItem, 'refUUID'>>,
    ) => {
      const item = state.items.find(
        (item) => item.refUUID === action.payload.refUUID,
      )
      if (!item) {
        return
      }
      item.state = 'pristine'
      updateItemState(item)
    },
    reorderStage: (
      state,
      action: PayloadAction<{ stage: ReferralStage; refUUIDs: string[] }>,
    ) => {
      // Compare with the current sort order to see if we need to update
      // Sortable is triggering this action on the initial load, so we need to check
      const current = state.items
        .filter((item) => item.stage === action.payload.stage)
        .map((item) => item.refUUID)
      if (current.join(',') === action.payload.refUUIDs.join(',')) {
        return
      }
      // Reorder the given items (assuming they are within the stage)
      let sortOrder = referralStageService.getBaseSortOrder(
        action.payload.stage,
      )
      for (const refUUID of action.payload.refUUIDs) {
        const item = state.items.find((item) => item.refUUID === refUUID)
        if (!item) {
          continue
        }
        item.sortOrder = sortOrder
        sortOrder += 10
      }
      // rearrange items
      updateSortOrder(state)
      updateStates(state)
    },
    removeItem: (
      state,
      action: PayloadAction<Pick<ReferralStatusesItem, 'refUUID'>>,
    ) => {
      state.items = state.items.filter(
        ({ refUUID }) => refUUID !== action.payload.refUUID,
      )
      updateSortOrder(state)
      updateStates(state)
    },
    reschedule: (
      state: ReferralStatusesState,
      action: PayloadAction<NodeJS.Timeout>,
    ) => {
      clearTimeout(state.updateTimeoutReference)
      for (const item of state.items) {
        if (item.state === 'modified') {
          item.state = 'scheduled'
        }
      }
      state.updateTimeoutReference = action.payload
    },
    restartItemState: (
      state: ReferralStatusesState,
      action: PayloadAction<Pick<ReferralStatusesItem, 'refUUID'>>,
    ) => {
      const item = state.items.find(
        (item) => item.refUUID === action.payload.refUUID,
      )
      if (!item || item.state === 'scheduled' || item.state === 'processing') {
        return
      }
      item.state = 'pristine'
      updateItemState(item)
    },
    setItems: (
      state: ReferralStatusesState,
      action: PayloadAction<ReferralStatusEntity[]>,
    ) => {
      state.items = action.payload.map((entity) => {
        return {
          refUUID: generateUUID(),
          state: 'pristine',
          referralStatusEntity: entity,
          name: entity.name,
          editName: false,
          sortOrder: entity.sortOrder,
          isFirst: true,
          isLast: true,
          stage: entity.stage,
          error: undefined,
        }
      })
      updateSortOrder(state)
      updateStageStats(state)
    },
    setItemState: (
      state: ReferralStatusesState,
      action: PayloadAction<Pick<ReferralStatusesItem, 'refUUID' | 'state'>>,
    ) => {
      const item = state.items.find(
        (item) => item.refUUID === action.payload.refUUID,
      )
      if (!item) return
      item.state = action.payload.state
    },
    updateItem: (
      state: ReferralStatusesState,
      action: PayloadAction<ReferralStatusesItemPartial>,
    ) => {
      const item = state.items.find(
        (item) => item.refUUID === action.payload.refUUID,
      )
      if (!item) return
      Object.assign(item, action.payload)
      updateItemState(item)
    },
  },
})

function updateSortOrder(state: ReferralStatusesState) {
  // sort
  state.items.sort((a, b) => a.sortOrder - b.sortOrder)
  // update numbers in stages
  let currentStage: ReferralStage | null = null
  let currentLocalSortOrder = 0
  for (let i = 0; i < state.items.length; i++) {
    const item = state.items[i]!
    item.isFirst = false
    item.isLast = false
    if (item.stage !== currentStage) {
      currentStage = item.stage
      currentLocalSortOrder = 0
      item.isFirst = true
      if (i > 0) {
        state.items[i - 1]!.isLast = true
      }
    }
    item.sortOrder =
      referralStageService.getBaseSortOrder(currentStage) +
      currentLocalSortOrder
    currentLocalSortOrder += 10
  }
  state.items[state.items.length - 1]!.isLast = true
}

function updateItemState(item: ReferralStatusesItem) {
  if (
    item.state === 'scheduled' ||
    item.state === 'processing' ||
    item.state === 'saved'
  ) {
    return
  }
  let hasDifference = false
  if (item.name != item.referralStatusEntity?.name) {
    hasDifference = true
  }
  if (item.sortOrder != item.referralStatusEntity?.sortOrder) {
    hasDifference = true
  }
  if (hasDifference) {
    if (item.referralStatusEntity) {
      item.state = 'modified'
    } else if (item.name.trim() === '') {
      // keep the new state if there is no name
      item.state = 'new'
    } else {
      item.state = 'modified'
    }
  } else {
    item.state = 'pristine'
  }
}

function updateStates(state: ReferralStatusesState) {
  for (const item of state.items) {
    updateItemState(item)
  }
}

function updateStageStats(state: ReferralStatusesState) {
  const stageStats = { ...INITIAL_STAGE_COUNTS }
  for (const item of state.items) {
    stageStats[item.stage] += 1
  }
  state.stageCounts = stageStats
}

export const referralStatusesReducer = referralStatuses.reducer
export const selectReferralStatuses = (state: RootState) =>
  state.referralStatuses

export const {
  addNewItem,
  detectItemState,
  reorderStage,
  removeItem,
  reschedule,
  restartItemState,
  setItems,
  setItemState,
  updateItem,
} = referralStatuses.actions
